import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_STATE_KEY, toRouterPath } from './routes';

/**
 * Anchor that renders the real Joomla SEF href (crawlable, right-click-friendly,
 * open-in-new-tab) but performs a client-side navigation, carrying the canonical
 * route on history state so the shell resolves the screen without a full reload.
 */
export function RoutedAnchor({
  href,
  to,
  className,
  title,
  children,
  ...aria
}: {
  /** The real Joomla SEF URL (used as the anchor href). */
  href?: string;
  /** The canonical client route stashed on history state. */
  to: string;
  className?: string;
  title?: string;
  children: ReactNode;
  'aria-label'?: string;
  'aria-current'?: 'page';
}) {
  const navigate = useNavigate();
  const target = href ? toRouterPath(href, to) : to;

  return (
    <a
      {...aria}
      className={className}
      href={href || to}
      title={title}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        navigate(target, { state: { [ROUTE_STATE_KEY]: to } });
      }}
    >
      {children}
    </a>
  );
}
